// @ts-nocheck
import * as React from 'react';
// TopBar — top navigation bar with school info, breadcrumbs, search, actions.

import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { COUNSELLOR } from '../../data/constants';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useAuthenticationSlice } from 'features/Authentication/slice';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { resetLocalStorage } from 'features/Authentication/slice/saga';

const getInitials = (name?: string) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return COUNSELLOR.initials;
  }

  return words
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
};

function DefaultActions() {
  const navigate = useNavigate();
  const authData: any = useAuth();
  const dispatch = useDispatch();
  const { actions } = useAuthenticationSlice();
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const displayName = authData?.NAME || authData?.USER_FIRSTNAME || authData?.USER_NAME || COUNSELLOR.name;
  const roleName = authData?.STAFF_ROLE?.STAFF_ROLE_NAME || authData?.USER_ROLE_NAME || COUNSELLOR.role;
  const initials = getInitials(displayName);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    const cancelToken = axios.CancelToken.source();
    if (authData?.USER_LOGIN_ID) {
      dispatch(actions.logoutStart({
        USER_LOGIN_ID: authData.USER_LOGIN_ID,
        cancelToken,
      }));
      return;
    }

    resetLocalStorage();
    dispatch(actions.clearState());
    window.location.href = '/';
  };

  const handleUserRolesSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsUserMenuOpen(false);
    navigate('/user-roles');
  };

  const handleLogoutSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsUserMenuOpen(false);
    handleLogout();
  };

  return (
    <div className="topbar__actions">
      <div className="topbar__search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" /><line x1="20" y1="20" x2="16" y2="16" />
        </svg>
        <span>Search enquiries, parents, AI features...</span>
        <kbd>⌘K</kbd>
      </div>
      <button className="icon-btn" title="Notifications">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
        <span className="icon-btn__dot" />
      </button>
      <div className="topbar__user-menu" ref={userMenuRef}>
        <button
          className="topbar__user"
          type="button"
          aria-haspopup="menu"
          aria-expanded={isUserMenuOpen}
          onClick={() => setIsUserMenuOpen(value => !value)}
        >
          <div className="avatar" title={displayName}>{initials}</div>
          <div className="topbar__user-meta">
            <span>{displayName}</span>
            <small>{roleName}</small>
          </div>
          <svg className="topbar__user-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {isUserMenuOpen && (
          <div className="topbar__user-dropdown" role="menu">
            <button
              type="button"
              role="menuitem"
              onPointerDown={handleUserRolesSelect}
              onMouseDown={handleUserRolesSelect}
              onClick={handleUserRolesSelect}
            >
              User Roles
            </button>
            <button
              type="button"
              role="menuitem"
              onPointerDown={handleLogoutSelect}
              onMouseDown={handleLogoutSelect}
              onClick={handleLogoutSelect}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TopBar({
  crumbTitle = "Counsellor Console",
  rightSlot,
  onHome,
}: {
  crumbTitle?: string;
  rightSlot?: ReactNode;
  onHome?: () => void;
  activeParent?: unknown;
  concept?: unknown;
}) {
  const authData: any = useAuth();
  const schoolName = authData?.INSTITUTE_NAME || COUNSELLOR.school;
  const schoolMark = getInitials(schoolName);
  const schoolSub = [authData?.BRANCH_NAME, authData?.BRANCH_ADDRESS].filter(Boolean).join(' · ') || COUNSELLOR.schoolSub;

  return (
    <div className="topbar">
      <div
        className={`topbar__school${onHome ? ' topbar__home-link' : ''}`}
        onClick={onHome}
        onKeyDown={(event) => { if (onHome && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onHome(); } }}
        role={onHome ? 'link' : undefined}
        tabIndex={onHome ? 0 : undefined}
        title={onHome ? 'Go to Admissions Assured home' : undefined}
      >
        <div className="topbar__school-mark">{schoolMark}</div>
        <div className="topbar__school-meta">
          <div className="topbar__school-name">{schoolName}</div>
          <div className="topbar__school-sub">{schoolSub}</div>
        </div>
      </div>
      <div className="topbar__divider" />
      <div className="topbar__crumb">
        {onHome ? <button className="topbar__crumb-home" type="button" onClick={onHome}>Admissions Assured</button> : <span className="topbar__crumb-eyebrow">Admissions Assured</span>}
        <span style={{ color: "var(--fg-tertiary)", margin: "0 6px" }}>/</span>
        <span className="topbar__crumb-title">{crumbTitle}</span>
      </div>
      <div className="topbar__spacer" />
      {rightSlot || <DefaultActions />}
    </div>
  );
}
